import { useCallback, useEffect, useRef, useState } from 'react';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { customersBillingApi } from '@/api/customersBilling.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import {
  clearSearch,
  fetchRecentCustomers,
  fetchWalkInCustomer,
  selectCustomer,
  setSearchLoading,
  setSearchResults,
} from '@/store/posCustomerSlice';
import type { CustomerResponse } from '@/types/customer';
import { formatCurrency } from '@/utils/format';
import { useKeyboardContext } from '@/core/keyboard';
import { usePosBilling } from '@/pages/pos/PosBillingContext';
import { PosQuickAddCustomerModal } from './PosQuickAddCustomerModal';

interface Props {
  currencyCode: string;
  onCustomerChange: (customerId: number | undefined) => void;
}

export function PosCustomerPanel({ currencyCode, onCustomerChange }: Props) {
  const dispatch = useAppDispatch();
  const { walkIn, selected, recent, searchResults, searchLoading, initializing } = useAppSelector(
    (s) => s.posCustomer,
  );
  const fmt = (n: number | undefined) =>
    n != null ? formatCurrency(n, currencyCode) : '—';

  const { setFocusZone, setStatusHint } = useKeyboardContext();
  const { quickCustomerOpen, setQuickCustomerOpen } = usePosBilling();
  const [query, setQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchWalkInCustomer());
    dispatch(fetchRecentCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (selected) {
      onCustomerChange(selected.walkIn ? selected.id : selected.id);
    }
  }, [selected, onCustomerChange]);

  const runSearch = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (trimmed.length < 2) {
        dispatch(clearSearch());
        return;
      }
      dispatch(setSearchLoading(true));
      try {
        const res = await customersBillingApi.search(trimmed);
        dispatch(setSearchResults(res.results));
      } catch {
        dispatch(setSearchResults([]));
      } finally {
        dispatch(setSearchLoading(false));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 200);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [searchResults]);

  const pick = (c: CustomerResponse) => {
    dispatch(selectCustomer(c));
    setQuery('');
    dispatch(clearSearch());
    setDropdownOpen(false);
  };

  const selectWalkIn = () => {
    if (walkIn) {
      dispatch(selectCustomer(walkIn));
      setQuery('');
      dispatch(clearSearch());
      setDropdownOpen(false);
    }
  };

  const showDropdown =
    dropdownOpen && (query.trim().length >= 2 || searchResults.length > 0 || searchLoading);

  const displayName = selected?.walkIn ? 'Walk-in' : selected?.name ?? 'Walk-in';
  const isRegistered = selected && !selected.walkIn;

  return (
    <div className="border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <PersonOutlineIcon className="shrink-0 text-primary-600" fontSize="small" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
              {initializing ? 'Loading…' : displayName}
            </p>
            {isRegistered && (
              <p className="truncate text-xs text-slate-500">
                {selected.phone}
                {selected.walletBalance != null && ` · Wallet ${fmt(selected.walletBalance)}`}
                {selected.loyaltyPointsBalance != null &&
                  ` · Points ${Number(selected.loyaltyPointsBalance).toFixed(0)}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button variant="secondary" className="!px-2 !py-1 text-xs" onClick={selectWalkIn}>
            Walk-in
          </Button>
          <Button
            variant="secondary"
            className="!px-2 !py-1 text-xs"
            onClick={() => setQuickCustomerOpen(true)}
            title="Quick add customer"
          >
            <PersonAddAlt1Icon sx={{ fontSize: 16 }} />
            Add
          </Button>
        </div>
      </div>

      <div className="relative">
        <Input
          ref={searchRef}
          id="pos-customer-search"
          placeholder="Search phone, name, or code"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setDropdownOpen(true);
          }}
          onFocus={() => {
            setDropdownOpen(true);
            setFocusZone('customer');
            setStatusHint('Customer ↑↓ · Enter select · Add for new');
          }}
          onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
          onKeyDown={(e) => {
            if (!searchResults.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlightIndex((i) => Math.min(i + 1, searchResults.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlightIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && searchResults[highlightIndex]) {
              e.preventDefault();
              pick(searchResults[highlightIndex]);
            }
          }}
        />
        {showDropdown && (
          <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
            {searchLoading && (
              <li className="px-3 py-2 text-xs text-slate-500">Searching…</li>
            )}
            {!searchLoading && query.trim().length >= 2 && searchResults.length === 0 && (
              <li className="px-3 py-2 text-xs text-slate-500">
                No match —{' '}
                <button
                  type="button"
                  className="font-medium text-primary-600"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setQuickCustomerOpen(true);
                    setDropdownOpen(false);
                  }}
                >
                  quick add
                </button>
              </li>
            )}
            {searchResults.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-left text-sm ${
                    i === highlightIndex
                      ? 'bg-primary-100 dark:bg-primary-900/40'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(c)}
                >
                  <span className="font-medium text-slate-900 dark:text-white">{c.name}</span>
                  <span className="block text-xs text-slate-500">
                    {c.phone}
                    {c.walletBalance != null && ` · ${fmt(c.walletBalance)}`}
                    {c.loyaltyPointsBalance != null &&
                      ` · ${Number(c.loyaltyPointsBalance).toFixed(0)} pts`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {recent.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {recent.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c)}
              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700 hover:border-primary-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300"
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      <PosQuickAddCustomerModal
        open={quickCustomerOpen}
        onClose={() => setQuickCustomerOpen(false)}
        initialPhone={query.replace(/\s+/g, '').trim()}
        onCreated={(c) => pick(c)}
      />
    </div>
  );
}
